import React, { useState, useEffect, useRef, useMemo, useReducer } from 'react';
import axios from 'axios';
import { useHistory, useLocation } from "react-router-dom";
import { fetchUser } from "../services/authService";
import { AuthProbe, AUTH_PROBE } from "../services/authProbe";
import { areArraysEqual, getCurrentResearcherUuid, researcherFullName, getLastSyncEventId } from '../utils/index.jsx';
import { resolveResearcherDepartments } from '../utils/departments';
import { addBreadcrumb } from '../utils/diagnostics/breadcrumbs';
import { SIGN_IN_ROUTE } from '../routes/urls';
import { Autocomplete, FormControl, FormControlLabel, FormLabel, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography, createFilterOptions } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { FieldWrapper } from '../components/general/mini_components';
import { useDispatch, useSelector } from 'react-redux';
import { getAgendasInvestigationAction, getDepartmentsInvestigationAction } from '../redux/actions/hospitalActions';
import { Color } from '@mui/lab';
import { IDepartment, IInsurance, IPatient, IResearcher, IUnit, IBox, DepartmentType } from '../constants/types';
import { INITIAL_SELECT } from '../components/general/SmartFields';
import { IService, IServiceInvestigation, RequestStatus } from '../pages/hospital/Request/types';
import { fetchProfileInfoAction } from '../redux/actions/profileActions';
import Loader from '../components/Loader';
import { CheckCircle, Done, EventBusy, MoneyOff, Paid } from "@mui/icons-material";
import { getServiceGeneralService } from '../services/agenda';
import { isArray, isObject, set } from 'lodash';
import { DocumentStatus } from '../pages/hospital/Billing/types';
import { ColourChip } from '../components/general/mini_components-ts';
import { documentStatusToColor } from '../utils/bill';
import { fetchSingleSubmissionsPatientInvestigationAction, fetchSubmissionsPatientInvestigationAction } from '../redux/actions/submissionsPatientActions';
import { deletePatientAction, fetchPatientsAction, incrementalSyncPatientsAction } from '../redux/actions/patientsActions';
import { getBoxesService } from '../services/agenda';

export interface SnackbarType{
    show: boolean;
    message?: string | undefined;
    severity?: Color | undefined;
    
}

export function useSnackBarState(){
    const [showSnackbar, setShowSnackbar] = useState<SnackbarType>({show : false});

    function handleCloseSnackbar(){
        setShowSnackbar({show:false});
    }

    return [showSnackbar, setShowSnackbar, handleCloseSnackbar] as const;
}

export function useRouter(initValue:any){
    const history = useHistory();
    return {
        pathname : initValue ? initValue : history.location.pathname,

    }
}

export function useLoadingMessage(){
    const investigations = useSelector((state:any) => state.investigations);
    const loading = useSelector((state:any) => state.investigations.loading);

    let message;

    switch(loading){
        case 1:
            message = "general.loading.investigations";
            break;
        case 2:
            message = "general.loading.decrypting";
            break;
        default:
            message = "general.loading.loading";
    }
   
    return [loading, (
        <>
            <Loader infoString={<Translate id={`${message}`} />} />
        </>
    )]
}

export function useProfileInfo(){
    const investigations = useSelector((state:any) => state.investigations);
    const profile = useSelector((state:any) => state.profile.info);
    const loadingProfile = useSelector((state:any) => state.investigations.loading | state.profile.loading);
    
    const dispatch = useDispatch();

    useEffect(() => {
        
        if(investigations.currentInvestigation){
            if(!profile && !loadingProfile){
                dispatch(fetchProfileInfoAction(investigations.currentInvestigation.uuid));
            }
        }
    }, [ investigations]);

    return { profile, loadingProfile }
}



export function useServiceGeneral(serviceType:number){
    const [servicesGeneral, setServicesGeneral] = useState<IService[]>([]);
    const [loadingServicesGeneral, setLoadingServicesGeneral] = useState(false);
    useEffect(() => {
        
            if (!servicesGeneral) {
                setLoadingServicesGeneral(true);
                getServiceGeneralService(serviceType)
                    .then(response => {
                        setServicesGeneral(response.services);
                        setLoadingServicesGeneral(true);
                    })
                    .catch(err => {
                        setLoadingServicesGeneral(true);
                    })
            } 
    }, []);

    return { servicesGeneral, loadingServicesGeneral }
}

// ... existing code ...

export function useServiceInvestigation(serviceType: number) {
    const investigations = useSelector((state: any) => state.investigations);
    const [services, setServices] = useState<IServiceInvestigation[]>([]);
    const [loadingServices, setLoadingServices] = useState(false);

    useEffect(() => {
        async function fetchServices() {
            if (investigations.currentInvestigation?.uuid) {
                setLoadingServices(true);
                try {
                    const response = await axios.get(
                        `${import.meta.env.VITE_APP_API_URL}/hospital/${investigations.currentInvestigation.uuid}/services/${serviceType}`,
                        { 
                            headers: { "Authorization": localStorage.getItem("jwt") }
                        }
                    );
                    
                    if (response.status === 200) {
                        setServices(response.data.servicesInvestigation);
                    }
                } catch (error) {
                    console.error('Error fetching services:', error);
                } finally {
                    setLoadingServices(false);
                }
            }
        }

        fetchServices();
    }, [investigations.currentInvestigation, serviceType]);

    return { services, loadingServices };
}


export function usePatientSubmission(idSubmission:number, uuidPatient:string){
    const investigations = useSelector((state:any) => state.investigations);
    const submissions = useSelector((state:any) => state.patientsSubmissions.data);
    const loadingSubmissions = useSelector((state:any) => state.patientsSubmissions.loading);
    const [submission, setSubmission] = useState<any>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        async function getSubmission() {
            await dispatch(
                fetchSingleSubmissionsPatientInvestigationAction(investigations.currentInvestigation.uuid, idSubmission, uuidPatient)
            );
        }
        if(investigations.data && investigations.currentInvestigation && idSubmission && !submission){
            const foundSubmission = findSubmission(idSubmission);
            if(foundSubmission === undefined){
                getSubmission();   
            }
            else{
                setSubmission(foundSubmission);
            }
        }    
    }, [idSubmission, submission]);   
    
    function findSubmission(idSubmission:number){
        if(submissions && idSubmission){
            const findSubmission = Object.values(submissions[uuidPatient]).flatMap((submissionsSurvey:any) => {
                return submissionsSurvey.submissions;
            }).find((subi:any) => subi.id === idSubmission);
        
            return findSubmission;
        }
    }

    useEffect(() => {
        const foundSubmission = findSubmission(idSubmission);
        if(foundSubmission !== undefined){
            setSubmission(foundSubmission);
        }
    }, [submissions])

    return [submission, loadingSubmissions];
}

// Export new coverage-based insurance hooks from separate file
export { usePatientInsurance, useInsurancesList, useCoveragesList, useSelectableCoverages, useCoverage } from './usePatientInsurance';

export function useDepartments(uuidResearchersDepartmentsOnly:string = "", onlyResearcherIfSingle:boolean = false){
    const investigations= useSelector((state:any) => state.investigations);
    const departments:IDepartment[] | null = useSelector((state:{hospital : {data: {departments : IDepartment[]}}}) => state.hospital.data.departments ? state.hospital.data.departments : null);
    const researchers:IResearcher[] = useSelector((state:any) => state.hospital.data.researchers ? state.hospital.data.researchers : []);
    const loadingDepartments:boolean = useSelector((state:any) => state.hospital.loading || state.investigations.loading);
    const [filteredDepartments, setFilteredDepartments] = useState<IDepartment[]>([]);

    // Only select the specific investigation properties we need
    const investigationUuid = useSelector((state:any) => 
        state.investigations.data && state.investigations.currentInvestigation 
            ? state.investigations.currentInvestigation.uuid 
            : null
    );

    const dispatch = useDispatch();

    useEffect(()=>{
        async function getDepartments(uuidInvestigation:string){
            await dispatch(
                getDepartmentsInvestigationAction(uuidInvestigation)
            ); 
        }
        if(investigationUuid && departments === null){
            getDepartments(investigationUuid)
        }
    }, [investigationUuid, departments, dispatch])

    useEffect(() => {
        if(departments !== null){
            let tempFiltered = [...departments];
            if(uuidResearchersDepartmentsOnly !== ""){
                const currentResearcher = researchers.find((researcher:any) => researcher.uuid === uuidResearchersDepartmentsOnly);
                // currentResearcher === undefined → resolveResearcherDepartments returns all,
                // matching the previous "researcher not found → keep all" behaviour.
                tempFiltered = resolveResearcherDepartments(departments, currentResearcher, onlyResearcherIfSingle);
            }
            setFilteredDepartments(tempFiltered);
        }
    }, [uuidResearchersDepartmentsOnly, departments, researchers, onlyResearcherIfSingle])

    return { departments: filteredDepartments, researchers, investigations, loadingDepartments}
}


export const useResearcher = (uuid: string) => {
    const { researchers, loadingDepartments : loading } = useDepartments();
    const [researcherFound, setResearcherFound] = useState<IResearcher | null>(null);

    useEffect(() => {
        const foundResearcher = researchers.find((researcher) => researcher.uuid === uuid);
        if(foundResearcher){
            setResearcherFound(foundResearcher)
        }
        else{
            setResearcherFound(null);
        }
    }, [researchers])
  
    return { 
        researcher : researcherFound, 
        loading,
        error: !researcherFound && !loading ? new Error('Researcher not found') : null
    };
  };

export function useResearchersByDepartmentType(departmentType: DepartmentType) {
    const investigations = useSelector((state: any) => state.investigations);
    const departments: IDepartment[] | null = useSelector((state: any) => 
        state.hospital.data.departments ? state.hospital.data.departments : null
    );
    const researchersAll:IResearcher[] = useSelector((state:any) => state.hospital.data.researchers ? state.hospital.data.researchers : []);
    const loadingDepartments = useSelector((state: any) => 
        state.hospital.loading || state.investigations.loading
    );
    const [researchers, setResearchers] = useState<IResearcher[]>([]);

    const dispatch = useDispatch();

    // Fetch departments if needed
    useEffect(() => {
        async function getDepartments(uuidInvestigation: string) {
            await dispatch(getDepartmentsInvestigationAction(uuidInvestigation));
        }
        if (investigations.data && investigations.currentInvestigation && departments === null) {
            getDepartments(investigations.currentInvestigation.uuid);
        }
    }, [investigations, departments, dispatch]);

    // Process departments to get unique researchers
    useEffect(() => {
        if (departments) {
            // Get all researchers from departments of the specified type
            const researchersMap = new Map<string, IResearcher>();
            
            departments
                .filter(department => department.type === departmentType)
                .forEach(department => {
                    department.units.forEach(unit => {
                        researchersAll.filter((researcher) => {
                            if(researcher.units.find((unitResearcher) => unitResearcher.uuid === unit.uuid)){
                                researchersMap.set(researcher.uuid, researcher);
                            }
                        })
                    });
                });

            setResearchers(Array.from(researchersMap.values()));
        }
    }, [departments, departmentType]);

    return {
        researchers,
        loadingDepartments,
        isEmpty: researchers.length === 0
    };
}   


export { useRequest } from './useRequest';

export function useSelectSmartField(initialState:any, label:string, type:string, errorState:boolean, setAddingSmartField:(adding:boolean) => void){
    //Hay que devolver undefined para que el select no ponga el placeholder arriba
    const [addSmartField, setAddSmartField] = useState(initialState && initialState.addSmartField ? initialState.addSmartField : !INITIAL_SELECT.includes(type) ? true : undefined);

    function selectChanged(event:React.ChangeEvent<{
        name?: string | undefined;
        value: unknown;
    }>){
        console.log(event);
        setAddSmartField(event.target.value);
    }
    function resetState(){
        setAddSmartField(!INITIAL_SELECT.includes(type));
        setAddingSmartField(true);
    }
    function renderSelect(){
        const optionsArray = [<MenuItem value={1}><Translate id="general.yes" /></MenuItem>, <MenuItem value={0}><Translate id="general.no" /></MenuItem>]
        
        return(
            <FieldWrapper>
                <FormControl variant="outlined" fullWidth margin="dense" error={errorState}>
                    <InputLabel id="show_treatment">{label}</InputLabel>
                    <Select
                        labelId="show_treatment"
                        id="show_treatment"
                        label={label}
                        value={addSmartField}
                        onChange={(event) => selectChanged(event)}
                    >
                    { optionsArray }
                    </Select>
                </FormControl>
            </FieldWrapper>
        )
    }
    // SHER-263 P6: setAddSmartField is exposed so the allergy first step (explicit
    // "Add new allergy" / "Mark as no known allergies" buttons that replace the
    // Yes/No dropdown) can drive the same state the dropdown used to set.
    return [addSmartField, renderSelect, resetState, setAddSmartField]
}


export function useSherwoodUser(){
    const history = useHistory();
    let location = useLocation();
    const initialLocation = location.pathname;
    const [isLoading, setIsLoading] = useState(true);
    const [isAuth, setIsAuth] = useState(true);
    const [error, setError] = useState(false);
    
    useEffect(() => {
     
        async function checkUser(){
            try{
                addBreadcrumb('state', 'useSherwoodUser.run', {
                    route: location.pathname,
                    previousUserExists: Boolean(localStorage.getItem("jwt")),
                });

                if(localStorage.getItem("jwt") !==""){
                    const authResult: AuthProbe = await fetchUser();

                    if(authResult === AUTH_PROBE.OK){
                        setIsAuth(true);
                        addBreadcrumb('state', 'useSherwoodUser.result', { ok: true, reason: 'authed' });
                    }
                    else if(authResult === AUTH_PROBE.UNAUTHORIZED){
                        // CHOM incident 2026-06-10 — only a real 401 expels.
                        setIsAuth(false);
                        addBreadcrumb('state', 'useSherwoodUser.result', { ok: false, reason: 'unauthorized' });
                    }
                    else{
                        // 'network' — transient failure: keep the current auth,
                        // do NOT redirect to login. A genuinely expired token
                        // yields 401 on the next real API call and logs out then.
                        addBreadcrumb('state', 'useSherwoodUser.result', { ok: true, reason: 'network-transient-keep-session' });
                    }
                }
                else{
                    setIsAuth(false);
                    addBreadcrumb('state', 'useSherwoodUser.result', { ok: false, reason: 'no-jwt' });
                }
                setIsLoading(false);
            }
            catch(error){
                console.log(error);
                setIsLoading(false);
                setIsAuth(false);
                setError(true);
                try {
                    addBreadcrumb('exception', 'useSherwoodUser.error', {
                        errorName: (error as any)?.name,
                        errorMessage: (error as any)?.message,
                    });
                } catch (_) { /* ignore */ }
            }
        }

        checkUser();

    }, [history])
    
    if(!isAuth){        
        history.push({
            pathname: SIGN_IN_ROUTE,
            state: { 
                from: history.location.pathname
            }
        })
    }
    

    return {isLoading, isAuth, error}
}

/**
 * A custom useEffect hook that only triggers on updates, not on initial mount
 * Idea stolen from: https://stackoverflow.com/a/55075818/1526448
 * @param {Function} effect
 * @param {Array<any>} dependencies
 */
 export function useUpdateEffect(effect:() => void, dependencies:any = []) {
    const didMount = useRef(false);

    useEffect(() => {
        if (didMount.current) effect();
        else didMount.current = true;
    }, dependencies);
  }

export function usePrevious(value:any, withInitial?:boolean){
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    if(ref.current === undefined && withInitial){
        if(isArray(value)){
            return [...value];
        }
        else if(isObject(value)){
            return {...value};
        }
        return value;
        
    }
    return ref.current as any;
}

export function useOffline(){
    const [offline, setOffline] = React.useState(!navigator.onLine);

    function updateOffline(){
        if(offline === navigator.onLine){
            setOffline(!navigator.onLine);
        }
    }
    useEffect(() => {
        async function subscribeToDB(){
            // const db = await openStore("workbox-background-sync");
            // var tx = db.transaction("requests", "readwrite");

            // tx.objectStore("requests").openCursor(0).onsuccess = function (event:any) {
            //     var cursor, object;

            //     cursor = event.target.result;
            //     object = cursor.value;
            //     object.value = 43;
            //     cursor.update(object).onsuccess = function (event:any) {
            //         db.transaction("requests").objectStore("requests").get(0).onsuccess = function (event:any) {
            //             console.log("Cursor update onsuccess event:");
            //             console.log(event.target.result);
            //         };
            //     };

            // };
        }
        window.addEventListener('online',  updateOffline);
        window.addEventListener('offline', updateOffline);
       
        subscribeToDB();
        
    }, [offline]);


    return offline;
}

export function useUnitSelector(units:IUnit[]){
    const [unitSelected, setUnitSelected] = React.useState<string | null>(null);
    const [errorUnit, setErrorUnit] = React.useState(false);

    useEffect(() => {
        if(units.length === 1){
            setUnitSelected(units[0].uuid as string);
        }
    }, [units])

    function markAsErrorUnit(){
        setErrorUnit(true);
    }

    function renderUnitSelector(){
        if(units.length === 1){
            return null;
        }
        else{
            const optionsArray = units.map((unit) => {
                return <MenuItem value={unit.uuid}>{unit.department.name} - {unit.name}</MenuItem>
            })
            return(
                <Grid item xs={12} style={{paddingTop:'0.5rem', paddingBottom:'0.5rem'}}>
                    <FormControl variant="outlined"  style={{minWidth: 220}} error={errorUnit} >
                    <InputLabel id="unit"><Translate id="pages.hospital.pharmacy.request.select_unit" /></InputLabel>
                        <Select 
                            labelId="unit"
                            id="unit"
                            label="unit"
                            value={unitSelected}
                            onChange={(event) => {
                                setErrorUnit(false);
                                setUnitSelected(event.target.value as string)}}
                        >
                        { optionsArray }
                        </Select>
                    </FormControl>
                </Grid>
            )
        }
        
    }
    return {unitSelected, renderUnitSelector, markAsErrorUnit}
}

export function useStatusDocument(status:DocumentStatus, editable:boolean = true){
    const [statusDocument, setStatusDocument] = React.useState<DocumentStatus>(status);

    const handleChangeStatus = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStatusDocument(Number((event.target as HTMLInputElement).value));
    };

    function renderStatusDocument(){
        let renderStatusDocument = null;
        if(status === DocumentStatus.CANCELLED){
            renderStatusDocument = <><Typography variant="body2" component='span' fontWeight='bold'><Translate id="hospital.billing.bill.status.label" /> : </Typography><ColourChip rgbcolor={documentStatusToColor(DocumentStatus.CANCELLED)} label={<Translate id="hospital.billing.bill.cancelled" />}/></>
        }
        else if(status === DocumentStatus.CLOSED){
            renderStatusDocument = <><Typography variant="body2" component='span' fontWeight='bold'><Translate id="hospital.billing.bill.status.label" /> : </Typography><ColourChip rgbcolor={documentStatusToColor(DocumentStatus.CLOSED)} label={<Translate id="hospital.billing.bill.closed" />}/></>
        }
        else{
            renderStatusDocument =  (
                <FormControl>
                <FormLabel id="demo-row-radio-buttons-group-label">
                        <Typography variant="body2" component='span' fontWeight='bold'><Translate id="hospital.billing.bill.status.label" /></Typography></FormLabel>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={statusDocument}
                    onChange={handleChangeStatus}
                >
                    <FormControlLabel value={DocumentStatus.DRAFT} control={<Radio />} label={<Typography variant="body2" component='span'><Translate id="hospital.billing.bill.draft" /></Typography>} />
                    <FormControlLabel value={DocumentStatus.CLOSED} control={<Radio />} label={<Typography variant="body2" component='span' ><Translate id="hospital.billing.bill.closed" /></Typography>} />
                </RadioGroup>
                </FormControl>
            );
        }
        return renderStatusDocument;
    }
    
    return {statusDocument, renderStatusDocument}
}
interface OptionSelector{
    label:string,
    value:string
}
// SHER-455: searchable autocomplete variant for long option lists (e.g. the
// billing doctor selector). Limits the visible matches so the dropdown never
// runs off-screen the way the plain <Select> did.
const autocompleteFilterOptions = createFilterOptions<OptionSelector>({
    limit: 15,
    stringify: (option) => option.label,
});

function useSelectLogic(optionsSelector:OptionSelector[], key:string, defaultValue?:string, canSelectNoOption:boolean = false, noReturnIfOnlyOne:boolean = false, useAutocomplete:boolean = false){
    const [optionSelected, setOptionSelected] = React.useState<string | null>(defaultValue ? defaultValue : "");
    const [error, setError] = React.useState(false);
    const [options, setOptions] = React.useState<OptionSelector[] | null>(null);

    useEffect(() => {
        if(options === null || !areArraysEqual(options, optionsSelector)){
            setOptions(optionsSelector);
        }
        
        if(optionsSelector && optionsSelector.length === 1){
            setOptionSelected(optionsSelector[0].value as string);
        }
    }, [optionsSelector])

    useEffect(() => {
        if(defaultValue && defaultValue !== ""){
            setOptionSelected(defaultValue);
        }
    }, [defaultValue])

    function markAsErrorCallback(){
        setError(true);
    }

    function renderSelector(){
        if(!options){
            return <Loader />
        }
        if(options.length === 0){
            if(canSelectNoOption){
                return null;
            }
            return <Typography variant="h4" gutterBottom><Translate id={`general.use_selector.missing_option.${key}`} /></Typography>
            
        }
        else if(options.length === 1){
            if(noReturnIfOnlyOne){
                return null;
            }
            return(
                <>
                    <Typography variant="h4" gutterBottom><Translate id={`general.use_selector.label.${key}`} />:</Typography>
                    <Typography variant="h4" gutterBottom >{options[0].label}</Typography>
                </>
            )
            //return null;
        }
        else{
            const sortedOptions = [...options].sort((a, b) => a.label.localeCompare(b.label));
            if(useAutocomplete){
                const selectedOption = sortedOptions.find((option) => option.value === optionSelected) || null;
                return(
                    <Grid item xs={12} style={{paddingTop:'0.5rem', paddingBottom:'0.5rem'}}>
                        <Autocomplete
                            id={key}
                            fullWidth
                            options={sortedOptions}
                            value={selectedOption}
                            filterOptions={autocompleteFilterOptions}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            onChange={(event, newValue) => {
                                setError(false);
                                setOptionSelected(newValue ? (newValue.value as string) : "");
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    error={error}
                                    label={<Translate id={`general.use_selector.select.${key}`} />}
                                />
                            )}
                            sx={{ minWidth: 220 }}
                        />
                    </Grid>
                )
            }
            let optionsArray = sortedOptions.map((option) => {
                return <MenuItem value={option.value}>{option.label}</MenuItem>
            })
            if(canSelectNoOption){
                optionsArray = [<MenuItem value={"all"}><Translate id={`general.use_selector.all.${key}`} /></MenuItem>, ...optionsArray]
            }
            return(
                <Grid item xs={12} style={{paddingTop:'0.5rem', paddingBottom:'0.5rem'}}>
                    <FormControl variant="outlined"  style={{minWidth: 220}} error={error} >
                    <InputLabel id={key}><Translate id={`general.use_selector.select.${key}`} /></InputLabel>
                        <Select 
                            labelId={key}
                            id={key}
                            label={key}
                            value={optionSelected}
                            onChange={(event) => {
                                setError(false);
                                setOptionSelected(event.target.value as string)}}
                        >
                        { optionsArray }
                        </Select>
                    </FormControl>
                </Grid>
            )
        }
        
    }
    return { optionSelected, renderSelector, markAsErrorCallback}
}

export function useDeparmentsSelectorBis(defaultValue?:string, selectNoDepartment:boolean = false, uuidResearcher:string = "", noReturnIfOnlyOne:boolean = false){
    const { departments, loadingDepartments } = useDepartments(uuidResearcher);
    const [departmentSelected, setDepartmentSelected] = useState<IDepartment | null>(null);

    console.log("uuidresearcher", uuidResearcher);
    console.log("Departments", departments);
    const departmentOptions = useMemo(() => {
        return departments ? departments.map((department) => {
            return {
                label:department.name,
                value: department!.uuid as string
            }
        }) : [];
    }, [departments]);

    const { optionSelected, renderSelector, markAsErrorCallback} = useSelectLogic(departmentOptions, "department", defaultValue, selectNoDepartment, noReturnIfOnlyOne)

    useEffect(() =>{
        if(optionSelected){
            const findDepartment = departments?.find((department) => department.uuid === optionSelected);
            if(findDepartment){
                setDepartmentSelected(findDepartment);
            }
        }
        
    }, [optionSelected, departments])
    
    return {uuidDepartmentSelected:optionSelected, departments, departmentSelected, loadingDepartments,
            renderDepartmentSelector:renderSelector, 
            markAsErrorDepartmentCallback: markAsErrorCallback }
}

export function useDeparmentsSelector(selectNoDepartment:boolean = false, uuidResearchersDepartmentsOnly:string = "", noReturnIfOnlyOne:boolean = false, defaultValue?:string, onlyResearcherIfSingle:boolean = false){
    const { departments } = useDepartments(uuidResearchersDepartmentsOnly, onlyResearcherIfSingle);
    const [departmentSelected, setDepartmentSelected] = React.useState<string | null>(defaultValue ? defaultValue : null);
    const [errorDepartment, setErrorDepartment] = React.useState(false);

    useEffect(() => {
        if(departments && departments.length === 1 && !defaultValue){
            setDepartmentSelected(departments[0].uuid as string);
        }
    }, [departments])

    useEffect(() => {
        if(departmentSelected === ""){
            setDepartmentSelected(null);
        }
    }, [departmentSelected])

    function markAsErrorDepartmentCallback(){
        setErrorDepartment(true);
    }
    
    function renderDepartmentSelector(){
        if(!departments){
            return <Loader />
        }
        if(departments.length === 0){
            return null;
        }
        else if(departments.length === 1){
            if(noReturnIfOnlyOne){
                return null;
            }
            return(
                <>
                    <Typography variant="h4" gutterBottom>Department:</Typography>
                    <Typography variant="h4" gutterBottom >{departments[0].name}</Typography>
                </>
            )
            //return null;
        }
        else{
            let optionsArray = departments.sort((a, b) => a.name.localeCompare(b.name)).map((department) => {
                return <MenuItem value={department.uuid}>{department.name}</MenuItem>
            })
            if(selectNoDepartment){
                optionsArray = [<MenuItem value={"all"}><Translate id="hospital.departments.all_departments" /></MenuItem>, ...optionsArray]
            }
            return(
                <FormControl 
                    variant="outlined" 
                    fullWidth
                    error={errorDepartment}
                >
                    <InputLabel id="department">
                        <Translate id="pages.hospital.pharmacy.request.select_department" />
                    </InputLabel>
                    <Select 
                        labelId="department"
                        id="department"
                        label="department"
                        value={departmentSelected}
                        onChange={(event) => {
                            setErrorDepartment(false);
                            setDepartmentSelected(event.target.value as string)
                        }}
                    >
                        {optionsArray}
                    </Select>
                </FormControl>
            );
        }
        
    }
    return { departmentSelected, renderDepartmentSelector, markAsErrorDepartmentCallback, departments}
}

export function useResearchersSelector(defaultValue?:string, selectNoDepartment:boolean = false, noReturnIfOnlyOne:boolean = false){
    const { researchers, loadingDepartments } = useDepartments();
    const [researcherSelected, setResearcherSelected] = useState<IResearcher | null>(null);

    const researcherOptions = researchers ? researchers.map((researcher) => {
        return {
            label:researcherFullName(researcher),
            value: researcher!.uuid as string
        }
    }) : [];
    // SHER-455: the doctor/staff list is very long; render it as a searchable
    // autocomplete instead of a full <Select> dropdown.
    const { optionSelected, renderSelector, markAsErrorCallback} = useSelectLogic(researcherOptions, "researcher", defaultValue, selectNoDepartment, noReturnIfOnlyOne, true)

    useEffect(() =>{
        if(optionSelected){
            const findResearcher = researchers?.find((researcher) => researcher.uuid === optionSelected);
            if(findResearcher){
                setResearcherSelected(findResearcher);
            }
        }
        
    }, [optionSelected, researchers])
    
    return {uuidResearcherSelected:optionSelected, researcherSelected, researchers, loadingResearchers:loadingDepartments,
            renderResearcherSelector:renderSelector, 
            markAsErrorReseacherCallback: markAsErrorCallback }
}

export function useResearcherDepartmentSelector(defaultValueResearcher?:string, defaultValueDepartment?:string){
    
    const { researchers, loadingResearchers, renderResearcherSelector, uuidResearcherSelected, researcherSelected, markAsErrorReseacherCallback  } = useResearchersSelector(defaultValueResearcher);
    const { departments, loadingDepartments, renderDepartmentSelector, uuidDepartmentSelected, departmentSelected, markAsErrorDepartmentCallback } = useDeparmentsSelectorBis(defaultValueDepartment, false, uuidResearcherSelected ? uuidResearcherSelected : "", false);
    console.log(departments);
    function renderResearcherDepartmentSelector(){
        const researcherSelector = renderResearcherSelector();
        if (!researcherSelected) {
            return researcherSelector;
        }
        return (
            <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                    {researcherSelector}
                </Grid>
                <Grid item xs={12} md={6}>
                    {renderDepartmentSelector()}
                </Grid>
            </Grid>
        );
    }
    return {researchers, renderResearcherDepartmentSelector, uuidResearcherSelected,
            departments,
            loadingResearcherOrDepartments:(loadingDepartments || loadingResearchers), 
            researcherSelected, uuidDepartmentSelected, departmentSelected,  
            markAsErrorReseacherCallback, markAsErrorDepartmentCallback}
}

const usePageVisibility = (onVisible: () => void) => {
    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          onVisible();
        }
      };
  
      document.addEventListener('visibilitychange', handleVisibilityChange);
  
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }, [onVisible]);
  };
  
  export default usePageVisibility;

  export function useDeletePatient(uuidInvestigation: string, uuidPatient: string) {
    const [deletedPatient, setDeletedPatient] = useState(false);
    const dispatch = useDispatch();

    // SHER-724: `reason` is mandatory — the API rejects a request without one.
    // The error is re-thrown rather than only logged so the caller's reason prompt can
    // surface it and keep the modal open; swallowing it left the user looking at a
    // dialog that appeared to have done nothing.
    //
    // There is deliberately no loading flag here: the reason prompt already owns the
    // in-flight state (it disables its own field and blocks close while submitting), and
    // a second copy is what unmounted the modal the error had to be shown in.
    async function deletePatientService(reason: string) {
        try {
            await dispatch(deletePatientAction(uuidInvestigation, uuidPatient, reason));
            setDeletedPatient(true);
        } catch (error) {
            console.error('Error deleting patient:', error);
            throw error;
        }
    }

    return { deletedPatient, deletePatientService };
}

  export function usePatients(uuidInvestigation:string){
    const patients:IPatient[] = useSelector((state: any) => state.patients.data ? state.patients.data[uuidInvestigation] : null) ;
    const loadingPatients = useSelector((state: any) => state.patients.loading);
    const progress = useSelector((state: any) => state.patients.progress);
    const investigations = useSelector((state: any) => state.investigations);
    const dispatch = useDispatch();

    useEffect(() => {
        async function syncPatients() {
            if (investigations.currentInvestigation?.uuid) {
                // Check if we already have patients loaded
                const hasPatients = patients && patients.length > 0;
                const uuidInv = investigations.currentInvestigation.uuid;
                const lastEventId = getLastSyncEventId(uuidInv);

                if (!hasPatients || lastEventId === null) {
                    // First time - do full sync
                    console.log('📥 Initial full sync - no patients loaded yet');
                    await dispatch(
                        fetchPatientsAction(investigations.currentInvestigation.uuid)
                    );
                } else {
                    // We have patients - do incremental sync
                    console.log('🔄 Incremental sync - fetching only new changes');
                    await dispatch(
                        incrementalSyncPatientsAction(
                            investigations.currentInvestigation.uuid, 
                            false  // silent = false to show loading state
                        )
                    );
                }
            }
        }

        if (investigations.currentInvestigation) {
            syncPatients();
        }
    }, [investigations.currentInvestigation]);

    return { patients, loadingPatients, progress };
}

export function useCurrentInvestigation() {
    const currentInvestigation = useSelector((state: any) => state.investigations.currentInvestigation);
    
    const hasFunctionality = (functionality: string): boolean => {
        return currentInvestigation?.functionalities?.includes(functionality) || false;
    };
    
    const isBillingEnabled = (): boolean => {
        return hasFunctionality("BILLING");
    };
    
    // SHER-1101: gates every PDF export/download control in the Hospital product. An opt-out:
    // on for every tenant unless its Investigation carries PDF_EXPORT_DISABLED.
    const isPdfExportEnabled = (): boolean => {
        return !hasFunctionality("PDF_EXPORT_DISABLED");
    };
    
    const isHospitalizationEnabled = (): boolean => {
        return hasFunctionality("HOSPITALIZATION");
    };
    
    const isOutpatientsEnabled = (): boolean => {
        return hasFunctionality("OUTPATIENTS");
    };
    
    const isRequestsEnabled = (): boolean => {
        return hasFunctionality("REQUESTS");
    };
    
    const isSurgeryEnabled = (): boolean => {
        return hasFunctionality("SURGERY");
    };
    
    const isPharmacyCentralEnabled = (): boolean => {
        return hasFunctionality("PHARMACY_CENTRAL");
    };
    
    const isShoeShopEnabled = (): boolean => {
        return hasFunctionality("SHOE_SHOP");
    };
    
    return {
        currentInvestigation,
        functionalities: currentInvestigation?.functionalities || [],
        hasFunctionality,
        isBillingEnabled,
        isPdfExportEnabled,
        isHospitalizationEnabled,
        isOutpatientsEnabled,
        isRequestsEnabled,
        isSurgeryEnabled,
        isPharmacyCentralEnabled,
        isShoeShopEnabled
    };
}

export function requestAppointmetToIcon(requestStatus:RequestStatus){
    let icon = null;
    switch (requestStatus) {
        case RequestStatus.COMPLETED:
            icon = <Done fontSize="small" />;
            break;
        case RequestStatus.ACCEPTED:
            icon = <CheckCircle fontSize="small" />;
            break;
        case RequestStatus.PENDING_PAYMENT:
        case RequestStatus.PENDING_PAYMENT_AFTER_VPA:
            icon = <MoneyOff fontSize="small" />;
            break;
        case RequestStatus.EXPIRED:
            icon = <EventBusy fontSize="small" />;
            break;
        case RequestStatus.CANCELED_BY_USER:
            icon = <EventBusy fontSize="small" />;
            break;
        default:
            return null;
    }
    return icon;
}

// Export PDF Configuration Hook
export { usePDFConfig } from './usePDFConfig';
